import {
    ContainerDataType,
    TaskConfigType,
    TaskResponseType,
    ContainerType,
    SupplementDataType,
    DecisionType,
    DecisionStatusType,
} from 'Types/Tasks.types'
import { IContainerStrategy } from './StateContainerCore/ContainerStrategy'
import { cloneDeep } from 'lodash'

export class TaskState<T extends ContainerType, U> {
    private taskState?: TaskResponseType<T, U>

    constructor(private containerStrategy: IContainerStrategy<T, U>) {}

    initTaskState = async (config: TaskConfigType<T, U>) => {
        const submissionId = config.assignment.submission?.id ?? config.submissionId
        if (!submissionId) {
            throw new Error('Cannot initialize task: Submission ID is missing')
        }

        this.taskState = {
            user_id: config.assignment.user_id,
            output_start_state_container: config.assignment.task.output_start_state_container,
            submission: {
                time_elapsed_in_seconds: config.assignment.submission?.time_elapsed_in_seconds ?? 0,
                id: submissionId,
                /**
                 * We start the task with the output of the task from the previous step
                 */
                draft_output_container: this.getInitialDraftContainer(config),
            },
        }
    }

    private getInitialDraftContainer(config: TaskConfigType<T, U>): ContainerDataType<T, U> {
        if (config.assignment.submission?.draft_output_container?.data) {
            return cloneDeep(
                config.assignment.submission!.draft_output_container
            ) as ContainerDataType<T, U>
        }

        if (config.assignment.task.output_start_state_container?.data) {
            const container = this.containerStrategy.getContainerFromOutputStart(
                config.assignment.task.output_start_state_container,
                config.mode
            )
            return container
        }

        return this.containerStrategy.getEmptyDraftContainer()
    }

    getOutputStartStateContainer(): ContainerDataType<T, U> | undefined {
        return this.taskState?.output_start_state_container
    }

    getCurrentState(): TaskResponseType<T, U> | undefined {
        return this.taskState
    }

    getDraftOutputContainer(): ContainerDataType<T, U> | undefined {
        return this.taskState?.submission.draft_output_container
    }

    updateDraftOutputContainer(container: ContainerDataType<T, U>) {
        if (!this.taskState) {
            throw new Error('Cannot update draft output container: Task state is not initialized')
        }
        this.taskState.submission.draft_output_container = container
    }

    clearDraftOutputContainer() {
        if (!this.taskState) {
            throw new Error('Cannot clear draft output container: Task state is not initialized')
        }
        this.taskState.submission.draft_output_container =
            this.containerStrategy.getEmptyDraftContainer()
    }

    getModifiedDraftOutputContainer(taskConfig: TaskConfigType<T, U>): ContainerDataType<T, U> {
        const draftOutputContainer = this.getDraftOutputContainer()
        if (!draftOutputContainer) {
            throw new Error(
                'Cannot get modified draft output container: Draft output container is not initialized'
            )
        }
        return this.containerStrategy.getModifiedDraftOutputContainer(
            draftOutputContainer,
            taskConfig
        )
    }

    setDecision(decision: DecisionType | null) {
        const draftOutputContainer = this.getDraftOutputContainer()
        if (!draftOutputContainer) {
            throw new Error('Cannot set decision: Draft output container is not initialized')
        }
        draftOutputContainer.decision = decision
    }

    createSupplement(stepName: string, comment: string, confidenceLevel?: string) {
        const draftOutputContainer = this.getDraftOutputContainer()
        if (!draftOutputContainer) {
            throw new Error('Cannot create supplement: Draft output container is not initialized')
        }
        draftOutputContainer.supplement = {
            schema_version: 1,
            step_name: stepName,
            comment: {
                data: {
                    value: comment,
                    value_type: 'STRING',
                },
            },
            confidence_level: confidenceLevel,
        }
    }

    updateSupplement(supplement: Partial<SupplementDataType>) {
        const draftOutputContainer = this.getDraftOutputContainer()
        if (!draftOutputContainer) {
            throw new Error('Cannot set supplement: Draft output container is not initialized')
        }
        if (!draftOutputContainer.supplement) {
            throw new Error('Cannot update supplement: Supplement is not initialized')
        }
        draftOutputContainer.supplement = {
            ...draftOutputContainer.supplement,
            ...supplement,
        }
    }

    createDecision(
        stepName: string,
        comment: string,
        status?: DecisionStatusType,
        rejectionReasons?: string[]
    ) {
        const draftOutputContainer = this.getDraftOutputContainer()
        if (!draftOutputContainer) {
            throw new Error('Cannot create decision: Draft output container is not initialized')
        }
        draftOutputContainer.decision = {
            schema_version: 1,
            status: status ?? DecisionStatusType.ACCEPT,
            supporting_comment: {
                data: {
                    value: comment,
                    value_type: 'STRING',
                },
            },
            step_name: stepName,
            submission_reasons: rejectionReasons,
        }
    }

    updateDecision(decision: Partial<DecisionType>) {
        const draftOutputContainer = this.getDraftOutputContainer()
        if (!draftOutputContainer) {
            throw new Error('Cannot update decision: Draft output container is not initialized')
        }
        if (!draftOutputContainer.decision) {
            throw new Error('Cannot update decision: Decision is not initialized')
        }
        draftOutputContainer.decision = {
            ...draftOutputContainer.decision,
            ...decision,
        }
    }
}
